const variableRegEx = /{{(.*?)}}/g;

export const removeDoubleStyles = (str) => {
  if (str)
    return str
      ?.replace(/\*\*(.*?)\*\*/g, "*$1*")
      ?.replace(/__(.*?)__/g, "_$1_")
      ?.replace(/~~(.*?)~~/g, "~$1~");

  return "";
};

/**
 * Normalize custom formatting markers in a string into corresponding HTML.
 * @param {string} str - Input string with custom formatting markers.
 * @returns {string} HTML-formatted string.
 */
export function replaceFormating(str, preview = false) {
  if (!str) return str;

  const text = normalizeLineBreaks(str);
  const lines = text.split('\n');
  let inList = false;
  let html = '';

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    const content = rawLine.replace(/\s+$/, '');

    // 1) Blank line → <br/>
    if (trimmed === '') {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += '<br/>';
      continue;
    }

    // 2) List item → <ul><li>…</li></ul>
    if (isListItem(trimmed)) {
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      const itemText = trimmed.slice(2).trimEnd();
      html += `<li>${applyInlineFormatting(itemText, preview)}</li>`;
      continue;
    }

    // 3) Normal paragraph → <p>…</p>
    if (inList) {
      html += '</ul>';
      inList = false;
    }
    html += `<p>${applyInlineFormatting(content, preview)}</p>`;
  }

  if (inList) html += '</ul>';
  return html;
}

/**
 * Replace mention syntax @[Name](id) with styled HTML span.
 * @param {string} text
 * @returns {string}
 */
function formatMentions(text) {
  return text.replace(
    /@\[(.+?)\]\(\d+\)/g,
    '<span style="background-color: #ffd84a; padding: 2px 6px; border-radius: 4px;">$1</span>'
  );
}

/**
 * Convert legacy markdown markers to placeholders to avoid conflicts.
 * @param {string} text
 * @returns {string}
 */
function replaceLegacyMarkdown(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '[[B]]$1[[/B]]')
    .replace(/__(.*?)__/g, '[[I]]$1[[/I]]')
    .replace(/~~(.*?)~~/g, '[[S]]$1[[/S]]');
}

/**
 * Apply standard markdown (bold, italic, strikethrough) on word boundaries.
 * @param {string} text
 * @returns {string}
 */
function formatStandardMarkdown(text) {
  return text
    .replace(/(?<=^|\s)\*(\S(.*?\S)?)\*(?=\s|$)/g, '<b>$1</b>')
    .replace(/(?<=^|\s)_(\S(.*?\S)?)_(?=\s|$)/g, '<i>$1</i>')
    .replace(/(?<=^|\s)~(\S(.*?\S)?)~(?=\s|$)/g, '<s>$1</s>');
}

/**
 * Restore legacy markdown placeholders back into HTML-wrapped markers.
 * @param {string} text
 * @returns {string}
 */
function restorePlaceholders(text) {
  return text
    .replace(/\[\[B\]\](.*?)\[\[\/B\]\]/g, '*<b>$1</b>*')
    .replace(/\[\[I\]\](.*?)\[\[\/I\]\]/g, '_<i>$1</i>_')
    .replace(/\[\[S\]\](.*?)\[\[\/S\]\]/g, '~<s>$1</s>~');
}

/**
 * Detect URLs (with or without protocol) and wrap them in clickable anchor tags with default link color (blue).
 * @param {string} text
 * @returns {string}
 */
function formatLinks(text, preview = false) {
  // Base TLDs + custom allowed ones
  const allowedTLDs = [
    'com', 'in', 'org', 'net', 'io', 'co', 'ai', 'dev', 'me',
    'info', 'biz', 'us', 'uk', 'ca', 'au', 'nz', 'de', 'fr', 'es',
    'it', 'nl', 'se', 'no', 'dk', 'fi', 'be', 'at', 'ch', 'jp', 'cn',
    'ru', 'br', 'mx', 'ar', 'za', 'ae', 'sa', 'sg', 'my', 'id', 'ph',
    'vn', 'th', 'kr', 'tr', 'il', 'pl', 'ng', 'eg', 'ke', 'ma', 'tn',
    'dz', 'ly', 'gh', 'ug', 'zm', 'zw', 'mz', 'bw', 'mu', 're', 'sc',
    'km', 'mg', 'rw', 'bi', 'mw', 'ao', 'na', 'sz', 'ls', 'gm', 'sl',
    'lr', 'gw', 'gn', 'sn', 'mr', 'ml', 'bf', 'bj', 'tg', 'ne', 'ci',
    'cm', 'cf', 'td', 'gq', 'cg', 'ga', 'st', 'dj', 'er', 'et', 'so',
    'sd', 'ss', 'cd', 'yt',

    // Added explicitly
    'abc', 'sq'
  ];

  const tldPattern = allowedTLDs.join('|');

  const regex = new RegExp(
    String.raw`(?<![\w@])((?:https?:\/\/)?(?:[\w-]+\.)+(${tldPattern})(?=[\/\s.,;!?]|$)(?:\/[\w\-./?%&=]*)?)|((?<![\w@])(https?:\/\/)?(?:\d{1,3}\.){3}\d{1,3}(?:\/[\w\-./?%&=]*)?)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(?:${tldPattern}))`,
    'gi'
  );

  return text.replace(regex, (match, domainUrl, tld, ipUrl, ipScheme, email) => {
    // Skip ftp://
    if (match.startsWith('ftp://')) return match;

    // Email match
    if (email) {
      const href = `mailto:${email}`;
      if (preview) {
        return `<a style="pointer-events: none; cursor: default; text-decoration: none;" onclick="return false;">${email}</a>`;
      }
      return `<a href="${href}" style="color: #1A0DAB; text-decoration: underline;">${email}</a>`;
    }

    // IP match (with or without http)
    if (ipUrl) {
      const href = ipUrl.startsWith('http') ? ipUrl : `https://${ipUrl}`;
      if (preview) {
        return `<a style="pointer-events: none; cursor: default; text-decoration: none;" onclick="return false;">${ipUrl}</a>`;
      }
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #1A0DAB; text-decoration: underline;">${ipUrl}</a>`;
    }

    // Domain match
    if (domainUrl) {
      const href = domainUrl.startsWith('http') ? domainUrl : `https://${domainUrl}`;
      if (preview) {
        return `<a style="pointer-events: none; cursor: default; text-decoration: none;" onclick="return false;">${domainUrl}</a>`;
      }
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #1A0DAB; text-decoration: underline;">${domainUrl}</a>`;
    }

    return match;
  });
}


/**
 * Main inline formatting pipeline: mentions, legacy markdown, standard markdown, restore placeholders, links.
 * @param {string} text
 * @returns {string}
 */
export function applyInlineFormatting(text, preview = false) {
  let result = text;
  result = formatMentions(result);
  result = replaceLegacyMarkdown(result);
  result = formatStandardMarkdown(result);
  result = restorePlaceholders(result);
  result = formatLinks(result, preview);
  return result;
}

/**
 * Replace all occurrences of custom newline marker ↵ with actual newline.
 * @param {string} text
 * @returns {string}
 */
function normalizeLineBreaks(text) {
  return text.replace(/↵/g, '\n');
}

/**
 * Check if a trimmed line is a list item (starts with "- ").
 * @param {string} line
 * @returns {boolean}
 */
function isListItem(line) {
  return line.startsWith('- ');
}


export const addLineBreake = (str) =>
  str?.length > 0 ? str?.replaceAll(/\n/g, "<br/>") : str;

export const checkMultipleNewLines = (str) => {
  // Match 2 or more consecutive empty/whitespace-only lines
  const invalidPattern = /(?:\n[ \t]*){2,}\n?/;

  if (!str) return false;
  const lines = str.split("\n");

  let blankLineCount = 0;
  for (let line of lines) {
    if (/^[ \t]*$/.test(line)) {
      blankLineCount++;
      if (blankLineCount > 1) {
        return true; // Invalid: more than one blank line in a row
      }
    } else {
      blankLineCount = 0; // Reset on non-blank line
    }
  }

  return false; // No invalid blank line patterns found
};

export const checkMaxNewLines = (str) =>
  str?.length > 0 ? str?.match(/\n{2,}(?!\S)/) : str;

export const checkVariable = (value) =>
  value?.toString()?.toLowerCase()?.match(variableRegEx);

export const replaceVariables = (str, value) => {
  const res = checkVariable(str);

  res?.map((r, i) => {
    str = str?.replaceAll(r, value?.[i]);
  });

  return str;
};

export const replaceVariablesForPreview = (str, variables) => {
  const res = checkVariable(str);

  res?.map((r, i) => {
    str = str?.replaceAll(r, variables?.[i]?.value);
  });

  return str;
};

export function formatPhoneNumber(countryCode, number) {
  if (!countryCode || !number) return "";

  // Remove any non-numeric characters for consistent formatting
  const cleanNumber = number.replace(/\D/g, "");

  // Add spaces after every 3rd digit (for the first three groups)
  const formattedNumber = cleanNumber.replace(
    /(\d{3})(\d{3})(\d{4})/,
    "$1 $2 $3"
  );

  // Return formatted with the country code
  return `${countryCode} ${formattedNumber}`;
}

export function formatInternationalPhone(rawPhone) {
  const countryCodes = [
    "91", "65", "60", "971", "1", "44", "61", "353", "94", "27",
    "974", "254", "234", "972", "63", "973", "965", "977", "64",
    "966", "66", "260", "880"
  ];

  // Try to match the longest possible country code
  const matchedCode = countryCodes
    .sort((a, b) => b.length - a.length)
    .find(code => rawPhone.startsWith(code));

  if (!matchedCode) {
    return rawPhone; // return as is if no valid country code is found
  }

  const localNumber = rawPhone.slice(matchedCode.length);

  // Format the local number into groups of 3-3-4 (typical structure)
  const formattedLocal = localNumber.replace(/^(\d{3})(\d{3})(\d{0,4})$/, (_, a, b, c) =>
    [a, b, c].filter(Boolean).join(' ')
  );

  return `${matchedCode} ${formattedLocal}`;
}

export function formatRelativeTime(timestamp) {
  const currentTime = new Date();
  const pastTime = new Date(timestamp);
  const diffMs = currentTime - pastTime;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMinutes < 60) {
    return `${diffMinutes} min`;
  }

  if (diffHours < 24) {
    return `${diffHours} hr`;
  }
  if (diffHours < 48 && diffHours > 24) {
    return 'Yesterday';
  }
  // if diff is within 7 days return day
  if (diffHours < 168 && diffHours > 48) {
    return pastTime.toLocaleDateString('en-US', {
      weekday: 'long',
    });
  }

  return pastTime.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}